import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Loader2,
  Plus,
  Search,
  ShieldCheck,
  Users,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { ColumnSelectorButton } from "@/components/ui/ColumnSelectorButton";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { RowActions } from "@/components/ui/RowActions";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { AppLayout } from "@/components/layout/AppLayout";
import { useRoleList, useDeleteRole } from "../hooks/useRoles";
import {
  useColumnVisibility,
  type ColumnDef,
} from "@/hooks/useColumnVisibility";
import type { RoleListItem } from "../types";

const ROLE_COLUMNS: ColumnDef[] = [
  { key: "name", label: "Name" },
  { key: "type", label: "Type" },
  { key: "status", label: "Status" },
  { key: "users", label: "Users" },
  { key: "description", label: "Description" },
];

type StatusFilter = "" | "active" | "inactive";
type TypeFilter = "" | "system" | "custom";

function TypeBadge({ isSystem }: { isSystem: boolean }) {
  return (
    <span
      className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium"
      style={
        isSystem
          ? { background: "hsl(210,55%,92%)", color: "hsl(210,100%,28%)" }
          : { background: "hsl(262,55%,92%)", color: "hsl(262,83%,38%)" }
      }
    >
      {isSystem ? "System" : "Custom"}
    </span>
  );
}

function StatusBadge({ active }: { active: boolean }) {
  return (
    <span
      className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium"
      style={
        active
          ? { background: "hsl(142,55%,92%)", color: "hsl(142,72%,28%)" }
          : { background: "hsl(210,16%,93%)", color: "hsl(215,16%,40%)" }
      }
    >
      {active ? "Active" : "Inactive"}
    </span>
  );
}

function RoleRow({
  role,
  onDelete,
}: {
  role: RoleListItem;
  onDelete: (id: string, name: string) => void;
}) {
  const navigate = useNavigate();
  return (
    <tr
      className="cursor-pointer transition-colors hover:bg-[hsl(210,50%,98%)]"
      onClick={() => navigate(`/admin/roles/${role.id}`)}
    >
      <td className="px-6 py-4">
        <div className="flex items-center gap-2.5">
          <div className="flex h-8 w-8 items-center justify-center rounded-md bg-[hsl(210,55%,92%)] text-[hsl(210,100%,28%)]">
            <ShieldCheck className="h-4 w-4" strokeWidth={1.5} />
          </div>
          <div className="font-medium text-foreground">{role.name}</div>
        </div>
      </td>
      <td className="px-6 py-4">
        <TypeBadge isSystem={role.isSystem} />
      </td>
      <td className="px-6 py-4">
        <StatusBadge active={role.isActive} />
      </td>
      <td className="px-6 py-4">
        <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
          <Users className="h-3.5 w-3.5" strokeWidth={1.5} />
          <span>{role.userCount}</span>
        </div>
      </td>
      <td className="px-6 py-4 text-sm text-muted-foreground">
        <span className="line-clamp-1">{role.description ?? "—"}</span>
      </td>
      <td className="px-6 py-4">
        <RowActions
          onView={() => navigate(`/admin/roles/${role.id}`)}
          onEdit={() => navigate(`/admin/roles/${role.id}`)}
          onDelete={() => onDelete(role.id, role.name)}
        />
      </td>
    </tr>
  );
}

export default function RoleListPage() {
  const { data: roles = [], isLoading, isError, error } = useRoleList();
  const deleteRole = useDeleteRole();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("");
  const [typeFilter, setTypeFilter] = useState<TypeFilter>("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(5);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);

  async function confirmDelete() {
    if (!deleteTarget) return;
    await deleteRole.mutateAsync(deleteTarget.id);
  }

  const {
    visibility,
    toggle: toggleColumn,
    reset: resetColumns,
    minReached,
  } = useColumnVisibility("roles", ROLE_COLUMNS);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return roles.filter((r) => {
      if (term && !r.name.toLowerCase().includes(term)) return false;
      if (statusFilter === "active" && !r.isActive) return false;
      if (statusFilter === "inactive" && r.isActive) return false;
      if (typeFilter === "system" && !r.isSystem) return false;
      if (typeFilter === "custom" && r.isSystem) return false;
      return true;
    });
  }, [roles, search, statusFilter, typeFilter]);

  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / limit));
  const safePage = Math.min(page, totalPages);
  const visible = filtered.slice((safePage - 1) * limit, safePage * limit);

  function resetPage<T>(setter: (v: T) => void) {
    return (value: T) => {
      setter(value);
      setPage(1);
    };
  }

  return (
    <AppLayout
      breadcrumb={[
        { label: "PhazeX Hub", href: "/" },
        { label: "Administration" },
        { label: "Role Management" },
      ]}
    >
      <div className="w-full space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ShieldCheck className="h-[18px] w-[18px] text-foreground" strokeWidth={1.5} />
            <h1 className="text-2xl font-semibold tracking-tight">Role Management</h1>
          </div>
          <div className="flex items-center gap-2">
            <ColumnSelectorButton
              columns={ROLE_COLUMNS}
              visibility={visibility}
              onToggle={toggleColumn}
              onReset={resetColumns}
              minReached={minReached}
            />
            <Button asChild>
              <Link to="/admin/roles/new">
                <Plus className="h-4 w-4" strokeWidth={1.5} />
                New Role
              </Link>
            </Button>
          </div>
        </div>

        {isError ? (
          <div className="rounded-xl border border-destructive/40 bg-destructive/5 p-6 text-sm text-destructive">
            <div className="font-medium">Failed to load roles</div>
            <div className="mt-1 font-mono text-xs opacity-80">
              {(error as Error)?.message ?? "Unknown error"}
            </div>
          </div>
        ) : (
          <>
            <div className="mb-3 flex flex-wrap items-center gap-3">
              <div className="relative w-[280px]">
                <Search
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                  strokeWidth={1.5}
                />
                <Input
                  placeholder="Search roles..."
                  value={search}
                  onChange={(e) => resetPage(setSearch)(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Select
                value={statusFilter}
                onChange={(e) =>
                  resetPage(setStatusFilter)(e.target.value as StatusFilter)
                }
                className="w-40"
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </Select>
              <Select
                value={typeFilter}
                onChange={(e) =>
                  resetPage(setTypeFilter)(e.target.value as TypeFilter)
                }
                className="w-40"
              >
                <option value="">All Types</option>
                <option value="system">System</option>
                <option value="custom">Custom</option>
              </Select>
            </div>

          <div
            className="overflow-hidden rounded-xl border bg-card"
            style={{ borderColor: "hsl(210,30%,85%)" }}
          >
            {isLoading ? (
              <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" strokeWidth={1.5} />
                Loading roles…
              </div>
            ) : visible.length === 0 ? (
              <div className="py-16 text-center text-sm text-muted-foreground">
                No roles match the current filters.
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-[hsl(210,30%,97%)] text-left text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-6 py-3">Role Name</th>
                    <th className="px-6 py-3">Type</th>
                    <th className="px-6 py-3">Status</th>
                    <th className="px-6 py-3">Users</th>
                    <th className="px-6 py-3">Description</th>
                    <th className="px-6 py-3 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {visible.map((role) => (
                    <RoleRow
                      key={role.id}
                      role={role}
                      onDelete={(id, name) => setDeleteTarget({ id, name })}
                    />
                  ))}
                </tbody>
              </table>
            )}

            <div data-pagination="true" style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '9px 14px',
              borderTop: '1px solid hsl(210,30%,85%)',
              background: 'hsl(210,30%,97%)',
              fontSize: '12px',
              color: 'hsl(215,16%,47%)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span>
                  Showing {total === 0 ? 0 : (safePage - 1) * limit + 1}–{Math.min(safePage * limit, total)} of {total} entries
                </span>
                <span style={{ marginLeft: '8px' }}>Show</span>
                <select
                  value={limit}
                  onChange={e => { setLimit(Number(e.target.value)); setPage(1) }}
                  style={{
                    height: '26px',
                    padding: '0 4px',
                    border: '1px solid hsl(210,30%,85%)',
                    borderRadius: '4px',
                    fontSize: '12px',
                    background: 'white',
                    cursor: 'pointer',
                  }}
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
                <span>entries</span>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <button
                  onClick={() => setPage(p => p - 1)}
                  disabled={safePage === 1}
                  style={{
                    width: '28px', height: '28px',
                    border: '1px solid hsl(210,30%,85%)',
                    borderRadius: '4px',
                    background: 'white',
                    cursor: safePage === 1 ? 'not-allowed' : 'pointer',
                    opacity: safePage === 1 ? 0.4 : 1,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >‹</button>
                <span style={{ padding: '0 10px' }}>
                  {Math.ceil(total / limit) === 0 ? '0' : safePage} / {Math.ceil(total / limit) || 1}
                </span>
                <button
                  onClick={() => setPage(p => p + 1)}
                  disabled={safePage >= Math.ceil(total / limit)}
                  style={{
                    width: '28px', height: '28px',
                    border: '1px solid hsl(210,30%,85%)',
                    borderRadius: '4px',
                    background: 'white',
                    cursor: safePage >= Math.ceil(total / limit) ? 'not-allowed' : 'pointer',
                    opacity: safePage >= Math.ceil(total / limit) ? 0.4 : 1,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >›</button>
              </div>
            </div>
          </div>
          </>
        )}

        <ConfirmDialog
          open={!!deleteTarget}
          onOpenChange={(o) => !o && setDeleteTarget(null)}
          title="Delete role?"
          description={
            deleteTarget
              ? `Are you sure you want to delete '${deleteTarget.name}'? It will be removed from your Roles list.`
              : ""
          }
          onConfirm={confirmDelete}
        />
      </div>
    </AppLayout>
  );
}
